import {observer} from "mobx-react";
import {Fragment} from "react";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontHeading4 from "../../../../../components/fonts/font-heading4/font-heading4";
import FontHeading5 from "../../../../../components/fonts/font-heading5/font-heading5";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import {FormFieldInfo} from "../../../../../components/form-field-info/form-field-info";
import MediaWithPlaceholder, {MEDIA_VARIANTS} from "../../../../../components/media-with-placeholder/media-with-placeholder";
import ProductCaseStore from "../../stores/product-case-store";
import ProductCaseDrawerBalance from "./rma-product-case-drawer-balance";
import IconWarning from "../../../../../components/icons/icon-warning/icon-warning";
import Collapsible from "../../../../../components/collapsible/collapsible";
import Currency from "../../../../../components/currency/currency";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../../../dashboard-translation/stores/dashboard-translations-store";

interface IProps{
    productCaseStore: ProductCaseStore;
}

const ProductCaseDrawerContents: React.FC<IProps> = observer(({productCaseStore}) => {

    const {getTranslation} = useDashboardTranslation();

    return (
        <>
            <FontHeading4 variant={FONT_VARIANT.BOLD}>
                {getTranslation('REQUEST_DETAILS', 'dashboard-returns-rma-card')}
            </FontHeading4>

            <Collapsible title={(
                <FormFieldInfo label={getTranslation('RETURN_REASON', 'dashboard-create-rma')}>
                    {productCaseStore.returnReasonStore.name}
                </FormFieldInfo>
            )}>
                {productCaseStore.questionsStores.length > 0
                    && productCaseStore.questionsStores.map((question, index) => {
                        const questionNumber = productCaseStore.questionsStores.length > 1 ? ` #${index + 1}` : '';
                        return (
                            <div className="question-answer-container" key={question.id}>
                                <FormFieldInfo label={`${getTranslation('QUESTION', 'dashboard-returns-rma-card')}${questionNumber}`}>
                                    {question.translation}
                                </FormFieldInfo>

                                <FormFieldInfo label={getTranslation('ANSWER', 'dashboard-returns-rma-card')}>
                                    {question.answers.length === 1
                                        ? question.answers[0].answer
                                        : question.answers.map((answer) => <Fragment key={answer.id}>{answer.answer}, </Fragment>)
                                    }
                                </FormFieldInfo>
                                {!!question.mediaList.length &&
                                    <div className={'question-media-list'}>
                                        {
                                            question.mediaList.map((media) => {
                                                return <MediaWithPlaceholder variant={MEDIA_VARIANTS.SMALL} enlargeSrc={media.src} key={media.id} mediaNotAvailable={!media.isSaved} src={media.src} />
                                            })
                                        }
                                    </div>
                                }
                            </div>
                        );
                    })
                }
            </Collapsible>

            <Collapsible title={(
                <FormFieldInfo label={getTranslation('POLICY_RULE','dashboard-create-rma')}>
                    <span className="label-with-icon">
                        {productCaseStore.isHiddenResolution && <IconWarning color="var(--color-accent-4)" />}
                        {productCaseStore.rmaOfferStore.policyRuleStore.displayName}
                    </span>
                </FormFieldInfo>
            )}>

                <FormFieldInfo label={getTranslation('RETURN_METHOD','dashboard-create-rma')}>
                    {productCaseStore.isHiddenResolution ? '--' : productCaseStore.returnMethodName}
                </FormFieldInfo>

                <FormFieldInfo label={getTranslation('PC_RESOLUTION', 'dashboard-returns-rma-card')}>
                    {productCaseStore.isHiddenResolution ? '--' : productCaseStore.rmaOfferStore.policyRuleStore.resolution.displayLabel}
                </FormFieldInfo>

                {!!productCaseStore.rmaOfferStore.policyRuleStore.isManualExchange && (
                    <div className="question-answer-container">
                        <FormFieldInfo label={getTranslation('QUESTION', 'dashboard-returns-rma-card')}>
                            {getTranslation('ITEM_EXCHANGE_QUESTION', 'dashboard-returns-rma-card')}
                        </FormFieldInfo>

                        <FormFieldInfo label={getTranslation('ANSWER', 'dashboard-returns-rma-card')}>
                            {productCaseStore.getManualExchangeMessage()}
                        </FormFieldInfo>
                        {!!productCaseStore.hasUploadedMediaFiles &&
                            <div className={'question-media-list'}>
                                {
                                    productCaseStore.uploadedMediaFiles.map((media, index) => {
                                        return <MediaWithPlaceholder variant={MEDIA_VARIANTS.SMALL} enlargeSrc={media.absolute_url} key={`uploaded_media_${index}`} mediaNotAvailable={!media.is_saved} src={media.absolute_url} />
                                    })
                                }
                            </div>
                            }
                    </div>
                )}

                {!!productCaseStore.exchangeStore &&
                    <FormFieldInfo label={getTranslation('NEW_ITEMS', 'dashboard-returns-rma-card')}>
                        <div className="exchange-info">
                            {productCaseStore.exchangeStore.image && (
                                <div className={"image-container"}>
                                    <MediaWithPlaceholder variant={MEDIA_VARIANTS.SMALL} alt={productCaseStore.exchangeStore.name} src={productCaseStore.exchangeStore.image} />
                                </div>
                            )}
                            <div className={'exchange-info-column'}>
                                <div><FontHeading5 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{productCaseStore.exchangeStore.name}</FontHeading5></div>
                                <div><FontBody2 color={FONT_COLOR.PRIMARY3_50}>{productCaseStore.exchangeStore.variantTitle}</FontBody2></div>
                                {productCaseStore.exchangeStore.sku
                                    ?
                                    (
                                        <div><FontBody2
                                            color={FONT_COLOR.PRIMARY3_50}>{`${getTranslation('SKU', 'dashboard-returns-rma-card')}: ${productCaseStore.exchangeStore.sku}`}</FontBody2>
                                        </div>
                                    )
                                    :
                                    ''
                                }

                                <div><FontBody2 color={FONT_COLOR.PRIMARY3_50}>{`${getTranslation('QUANTITY', 'dashboard-returns-rma-card')}: ${productCaseStore.exchangeStore.quantity}`}</FontBody2></div>
                            </div>
                        </div>
                    </FormFieldInfo>
                }
            </Collapsible>

            <div className="rma-page-product-case-drawer-balance-section">
                <Collapsible title={(
                    <div className="rma-page-product-case-drawer-balance-section-title">
                        <FontBody1 variant={FONT_VARIANT.BOLD}>{getTranslation('BALANCE', 'dashboard-returns-rma-card')}</FontBody1>
                        {productCaseStore.itemsBalanceStore.balance &&
                            <FontBody1 variant={FONT_VARIANT.BOLD}>
                                <Currency
                                    currencyCode={productCaseStore.itemsBalanceStore.balance.currency_code}
                                    amount={productCaseStore.itemsBalanceStore.balance.amount}
                                />
                            </FontBody1>}
                    </div>
                )}>
                    {(productCaseStore.itemsBalanceStore.showBalanceBreakdown && !dashboardTranslationsStore.isLoading) && <ProductCaseDrawerBalance itemsBalanceStore={productCaseStore.itemsBalanceStore} />}
                </Collapsible>
            </div>
        </>
    )
})

export default ProductCaseDrawerContents;